/**
 * Ocean structures: shipwrecks, ocean ruins and the ocean monument.
 */

import { SEA_LEVEL } from '../../constants';
import { GOLD_BLOCK_ID, TERRACOTTA_IDS, WATER_ID } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import {
  block, type Builder, cell, F, type Fill, int, pick, centredPiece, type Piece, type Rng, seedOf, type StructureType,
} from './kit';

export const OCEANS: ReadonlySet<Biome> = new Set([
  Biome.Ocean, Biome.DeepOcean, Biome.WarmOcean, Biome.LukewarmOcean, Biome.DeepLukewarmOcean, Biome.ColdOcean,
  Biome.DeepColdOcean, Biome.FrozenOcean, Biome.DeepFrozenOcean,
]);
const DEEP_OCEANS: ReadonlySet<Biome> = new Set([
  Biome.DeepOcean, Biome.DeepLukewarmOcean, Biome.DeepColdOcean, Biome.DeepFrozenOcean,
]);
const WARM: ReadonlySet<Biome> = new Set([Biome.WarmOcean, Biome.LukewarmOcean, Biome.DeepLukewarmOcean]);

// --- shipwreck ------------------------------------------------------------------

/** Hull woods with their mast log, as vanilla's shipwreck variants use. */
const WOODS: ReadonlyArray<readonly [string, string]> = [
  ['Oak Planks', 'Oak Log'], ['Spruce Planks', 'Spruce Log'], ['Dark Oak Planks', 'Dark Oak Log'],
  ['Jungle Planks', 'Jungle Log'],
];

/** A 5 x 16 hull, bow at v = 0: keel, sides, the raised stern deck, a broken mast
 *  and two chests (map and supply). Planks go missing at random — it is a wreck. */
function buildShipwreck(b: Builder, plank: number, mast: number): void {
  const y = b.y, r = b.rnd;
  const put = (u: number, yy: number, v: number): void => { if (r() > 0.14) b.set(u, yy, v, plank); };
  for (let v = 0; v < 16; v++) {
    const half = v < 1 || v > 14 ? 0 : v < 3 || v > 12 ? 1 : 2;
    for (let u = 2 - half; u <= 2 + half; u++) put(u, y + 1, v);               // keel and floor
    if (half > 0) for (let dy = 2; dy <= 3; dy++) { put(2 - half, y + dy, v); put(2 + half, y + dy, v); }
    else { put(2, y + 2, v); put(2, y + 3, v); }
    if (v >= 10 && half > 0) for (let u = 2 - half; u <= 2 + half; u++) put(u, y + 4, v); // stern deck
    if (v >= 11 && v <= 13 && half > 0) { put(2 - half, y + 5, v); put(2 + half, y + 5, v); }
  }
  const tall = 4 + int(r, 5);
  b.fill(2, y + 2, 6, 2, y + tall, 6, mast);
  b.chest(2, y + 2, 12, F.NV);
  b.chest(2, y + 2, 3, F.PV);
}

function shipwreck(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (!OCEANS.has(c.biome) && c.biome !== Biome.Beach) return null;
  const [p, m] = pick(rnd, WOODS);
  const plank = block(p), mast = block(m);
  return [centredPiece(x, z, int(rnd, 4), 5, 16, c.height, false, seedOf(rnd), (b) => buildShipwreck(b, plank, mast))];
}

// --- ocean ruins -----------------------------------------------------------------

const BRICKS = cell.mix([
  [block('Stone Bricks'), 5], [block('Mossy Stone Bricks'), 3], [block('Cracked Stone Bricks'), 2],
]);

/** One ruin: a broken floor and stumps of wall in stone brick (cold) or
 *  sandstone and terracotta (warm), with a chest in the middle. */
function buildRuin(b: Builder, w: number, d: number, warm: boolean): void {
  const y = b.y, r = b.rnd;
  const wall: Fill = warm
    ? cell.mix([[block('Sandstone'), 3], [TERRACOTTA_IDS.light_blue, 1], [block('Cut Sandstone'), 1]])
    : BRICKS;
  const floor = warm ? block('Sand') : block('Gravel');
  for (let v = 0; v < d; v++) {
    for (let u = 0; u < w; u++) {
      if (r() < 0.7) b.set(u, y, v, r() < 0.5 ? wall : floor);
      const edge = u === 0 || u === w - 1 || v === 0 || v === d - 1;
      if (edge) b.fill(u, y + 1, v, u, y + int(r, 4), v, wall);
    }
  }
  b.chest(w >> 1, y + 1, d >> 1, F.NV);
}

function oceanRuin(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (!OCEANS.has(c.biome)) return null;
  const warm = WARM.has(c.biome);
  const out: Piece[] = [];
  const n = 1 + int(rnd, 4);
  for (let i = 0; i < n; i++) {
    const px = x + (i === 0 ? 0 : int(rnd, 26) - 13), pz = z + (i === 0 ? 0 : int(rnd, 26) - 13);
    const h = g.columnClimate(px, pz).height;
    if (h >= SEA_LEVEL - 3) continue;
    const w = 5 + int(rnd, 4), d = 5 + int(rnd, 4);
    out.push(centredPiece(px, pz, int(rnd, 4), w, d, h, false, seedOf(rnd), (b) => buildRuin(b, w, d, warm)));
  }
  return out.length > 0 ? out : null;
}

// --- ocean monument --------------------------------------------------------------

const PRISMARINE = cell.mix([[block('Prismarine'), 3], [block('Prismarine Bricks'), 2]]);
const DARK = block('Dark Prismarine');
const LANTERN = block('Sea Lantern');
const BRICK = block('Prismarine Bricks');

/** A flooded prismarine room. */
function hall(b: Builder, u0: number, y0: number, v0: number, u1: number, y1: number, v1: number): void {
  b.room(u0, y0, v0, u1, y1, v1, PRISMARINE, WATER_ID);
  // dark prismarine trim along the roof edge, sea lanterns on the corners
  b.walls(u0, y1, v0, u1, y1, v1, DARK);
  for (const [u, v] of [[u0, v0], [u1, v0], [u0, v1], [u1, v1]]) b.set(u, y1, v, LANTERN);
}

/**
 * Ocean monument (vanilla OceanMonumentPieces), at two-thirds of vanilla's 58
 * blocks so it fits this world's halved ocean depth: a prismarine base on
 * legs, two wings reaching forward on either side of the entrance arch, the
 * long back hall, and the central tower under a stepped roof, with the gold
 * core — eight gold blocks in a dark prismarine vault — in its heart. Every
 * room is flooded.
 */
function buildMonument(b: Builder, top: number): void {
  const y = b.y;
  const S = 38; // footprint side
  b.fill(0, y + 1, 0, S, top, S, WATER_ID);
  // the base, standing on prismarine legs down to the sea floor
  b.fill(0, y, 0, S, y, S, BRICK);
  for (let v = 0; v <= S; v += 6) for (let u = 0; u <= S; u += 6) b.foundation(u, y - 1, v, DARK, 20);
  const h = top - y; // room for the building above the base
  const wing = Math.max(4, Math.floor(h * 0.45));
  // wings either side of the entrance, the back hall joining them
  hall(b, 0, y, 0, 10, y + wing, 26);
  hall(b, 28, y, 0, S, y + wing, 26);
  hall(b, 0, y, 24, S, y + wing + 1, S);
  // the entrance arch in the middle of the front
  hall(b, 14, y, 4, 24, y + wing - 1, 16);
  b.fill(17, y + 1, 4, 21, y + wing - 2, 4, WATER_ID);
  // central tower with a stepped roof
  hall(b, 12, y, 14, 26, y + h - 3, 32);
  for (let i = 0; i < 3; i++) b.fill(12 + i * 2, y + h - 2 + i, 14 + i * 2, 26 - i * 2, y + h - 2 + i, 32 - i * 2, PRISMARINE);
  b.set(19, top, 23, LANTERN);
  // the gold core
  b.room(16, y + 1, 20, 22, y + 5, 26, DARK, WATER_ID);
  b.fill(18, y + 2, 22, 19, y + 3, 23, GOLD_BLOCK_ID);
  // doorways between the rooms, and lantern pillars in the halls
  for (const [u, v] of [[10, 12], [28, 12], [19, 24], [12, 22], [26, 22]]) b.fill(u, y + 1, v, u, y + 3, v, WATER_ID);
  for (const [u, v] of [[5, 8], [33, 8], [5, 18], [33, 18], [8, 31], [30, 31]]) b.fill(u, y + 1, v, u, y + wing - 1, v, LANTERN);
  // keep the water column above the roof open
  b.fill(0, top + 1, 0, S, SEA_LEVEL - 1, S, WATER_ID);
}

function oceanMonument(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (!DEEP_OCEANS.has(c.biome)) return null;
  // the floor under the corners decides the base height: never above the shallowest
  let floor = c.height;
  for (const [dx, dz] of [[-19, -19], [19, -19], [-19, 19], [19, 19]]) {
    const h = g.columnClimate(x + dx, z + dz);
    if (!DEEP_OCEANS.has(h.biome)) return null;
    floor = Math.min(floor, h.height);
  }
  const y = floor + 1, top = SEA_LEVEL - 3;
  if (top - y < 12) return null;
  return [centredPiece(x, z, int(rnd, 4), 39, 39, y, false, seedOf(rnd), (b) => buildMonument(b, top))];
}

export const SHIPWRECK: StructureType = {
  name: 'Shipwreck', spacing: 24, separation: 4, salt: 165745295, reach: 1, generate: shipwreck,
};
export const OCEAN_RUIN: StructureType = {
  name: 'Ocean Ruin', spacing: 20, separation: 8, salt: 14357621, reach: 1, generate: oceanRuin,
};
export const OCEAN_MONUMENT: StructureType = {
  name: 'Ocean Monument', spacing: 32, separation: 5, salt: 10387313, reach: 2, generate: oceanMonument,
};
